import React from "react";
import mixpanel from "mixpanel-browser";
import {
  AnalyticsContext,
  AnalyticsEnabledEvent,
  AnalyticsEvent,
  AnalyticsProperties,
} from "./AnalyticsContext";
import {
  User,
  isInTrialMode,
  isUserClassLink,
  isUserClever,
  isUserExpired,
} from "../api";
import { PreferencesContext } from "./PreferencesContext";

interface Props {
  children: React.ReactNode;
}

export const AnalyticsProvider = (props: Props) => {
  const enabled = React.useRef(false);
  const { hasAcceptedData, hasEnabledAnalytics, enableAnalytics } =
    React.useContext(PreferencesContext);

  const superPropertiesFromUser = React.useCallback(
    (user: User): AnalyticsProperties => {
      // TODO: Add experiment properties, locale.
      const superProperties = {
        build_type: process.env.NODE_ENV === "production" ? "prod" : "dev",
        is_in_trial_mode: isInTrialMode(user),
        is_siwa: user.is_siwa,
        is_staff: user.is_guest,
        is_creator:
          user.profiles.find((profile) => profile.is_creator) !== undefined,
        is_expired: isUserExpired(user),
        is_clever: isUserClever(user),
        is_classlink: isUserClassLink(user),
      };
      return superProperties;
    },
    []
  );

  const logEvent = React.useCallback((event: AnalyticsEvent) => {
    if (!enabled.current) {
      return;
    }
    mixpanel.track(event.name, event.properties());
  }, []);

  const maybeLogAnalyticsEnabled = React.useCallback(() => {
    if (!hasEnabledAnalytics) {
      logEvent(new AnalyticsEnabledEvent());
      enableAnalytics();
    }
  }, [hasEnabledAnalytics, enableAnalytics, logEvent]);

  const maybeStartAnalytics = React.useCallback(() => {
    if (!enabled.current && hasAcceptedData) {
      const mixpanelKey =
        process.env.NODE_ENV === "production"
          ? "689fb96b8126c0042d8723e3540b4c25"
          : "f9e45462f796e9ad4baa21f458518a37";
      mixpanel.init(mixpanelKey, {
        debug: process.env.NODE_ENV !== "production",
        ip: false,
      });
      enabled.current = true;
      maybeLogAnalyticsEnabled();
    }
  }, [hasAcceptedData, maybeLogAnalyticsEnabled]);

  const identifyUser = React.useCallback(
    (user: User) => {
      if (!enabled.current) {
        return;
      }
      mixpanel.identify(user.id);
      mixpanel.register(superPropertiesFromUser(user));
    },
    [superPropertiesFromUser]
  );

  const resetUser = React.useCallback(
    (user: User) => {
      if (!enabled.current) {
        return;
      }
      mixpanel.reset();
      mixpanel.register(superPropertiesFromUser(user));
    },
    [superPropertiesFromUser]
  );

  const setIncludeIP = React.useCallback((includeIP: boolean) => {
    if (!enabled.current) {
      return;
    }
    mixpanel.set_config({ ip: includeIP });
  }, []);

  const registerSuperProperties = React.useCallback(
    (properties: AnalyticsProperties) => {
      if (!enabled.current) {
        return;
      }
      mixpanel.register(properties);
    },
    []
  );

  const contextValue = React.useMemo(
    () => ({
      maybeStartAnalytics,
      identifyUser,
      resetUser,
      logEvent,
      setIncludeIP,
      registerSuperProperties,
      superPropertiesFromUser,
    }),
    [
      maybeStartAnalytics,
      identifyUser,
      resetUser,
      logEvent,
      setIncludeIP,
      registerSuperProperties,
      superPropertiesFromUser,
    ]
  );

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {props.children}
    </AnalyticsContext.Provider>
  );
};
